import React, { useState, useEffect, useRef, Fragment } from 'react';
import { TextField as MuiTextField, Grid, Card, Toolbar, LinearProgress, CardContent, Table, TableHead, TableRow, TableBody, TableCell, Dialog, DialogContent, DialogActions, Typography, Button} from '@mui/material';
import axios from 'axios';
import Config from '../../../config';
import { Link } from 'react-router-dom';
import ExtendedSelectBase from '../../../components/select';
import AutoComplete from '../../../components/suggest_field';
import FastSelectBase from '../../../components/fast_suggest_field';
import DownloadButton from '../../../components/download_button';
import FileSaverBase from 'filesaver.js-npm';
import { ShareDialog, DeleteDialog, SaveDialog, SaveAsDialog, loadProfiles, saveProfile } from '../../files/import_export_profiles';
import withCatalogEnabled from './with_catalog_enabled';
import isEqual from 'lodash.isequal';
import { Form, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import DropZone from '../../files/dropzone';
import Fuse from 'fuse.js';
import { Notifier } from '../../documents/document_components';
import {parseCondition, getAttrOptions, RuleEditor, MappingEditor, RecursiveConditionField} from '../shared/mapping_templates';
import {isValidExpression, getErrorMessage} from '../shared/mapping_checker';
import {saveAttribute, AttributeEditor} from './catalog_attributes';
import Helpers from '../../../helpers';

// Relax untyped/forwardRef component + lib types to `any` (behavior unchanged).
const ExtendedSelect: any = ExtendedSelectBase;
const FastSelect: any = FastSelectBase;
const FileSaver: any = FileSaverBase;

const profileType = 'SupplierCatalog';

const setDefaultMappings = (profileFields, attributes) => {
    let mapped = [];
    // match based on direct field name
    for(let i = 0; i < attributes.length; i++){
        let attr = attributes[i];
        let field = profileFields.find(r => (r.field || '').toLowerCase() === attr.Name
        || (r.field || '').toLowerCase() === attr.FriendlyName);
        if(field && mapped.indexOf(field) === -1){
            field.alias = attr.Name;
            mapped.push(field);
        }
    }
    // match based on fuzzy logic
    let fuseOpts = {
        keys: ["Name", "FriendlyName"],
        minMatchCharLength: 4,
        threshold: 0.4,
        ignoreLocation: true
    };
    let fuse = new Fuse(attributes, fuseOpts);
    let unmatched = profileFields.filter(r => !r.alias);
    for(let i = 0; i < unmatched.length; i++){
        let field = unmatched[i];
        let fuseMatches: any = fuse.search(field.field);
        if(fuseMatches.length > 0 && profileFields.filter(r => r.alias === fuseMatches[0].item.Name).length === 0){
            field.alias = fuseMatches[0].item.Name;
        }
    }
}

const uploadFile = async (file, attributes) => {
    let fileType = 'csv';
    if(file.name && file.name.endsWith(".xlsx")){
        fileType = 'xlsx';
    }
    try{
        let formData = new FormData();
        formData.append("file", file);
        let parsed = (await axios.post(Config.api + `/odata/company/Functions.ParseFlatFile?docType=Catalog&preferredWorksheets=Catalog,Upload Template&fileType=${fileType}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
        })).data;
        let profile = {DocumentType: 'Catalog', Fields: parsed.Columns.map(r => ({field: r.Input, alias: r.Output, content: r.SampleContent}))};
        setDefaultMappings(profile.Fields, attributes);
        let oldProfile = {DocumentType: 'Catalog', Fields: parsed.Columns.map(r => ({field: r.Input, alias: r.Output, content: r.SampleContent}))};
        let parsedFields = parsed.Columns.map(r => ({field: r.Input, alias: r.Output, content: r.SampleContent, scientificNotation: r.ScientificNotation}));
        let validationWarnings = parsedFields.filter(r => !(!r.scientificNotation)).map(r => `Column ${r.field} appears to contain scientific notation. Sample value: ${r.scientificNotation}`);
        return {profile, oldProfile, parsedFields, validationWarnings, originalAttributes: parsedFields.map(r => ({label: r.field, value: r.field}))};
    } catch (e){
        let errors = ["Unexpected error occurred."];
        if(e.response){
            errors = new Helpers().getApiErrors(e.response.data);
        }
        let errorMsg = errors.join(" ");
        throw new Error(`Unable to parse the uploaded file. ${errorMsg}`);
    }
}

const importFile = async (file: any, profile: any, exportIfAble?: any) => {
    try{
        var formData = new FormData();
        formData.append("file", file);
        let columnMappings = (profile && profile.Fields) ? profile.Fields.filter(r => r.alias) : [];
        let request = {Columns: [], Rules: profile.Rules, ExportIfAble: !(!exportIfAble)};
        if(columnMappings.length > 0){
            request.Columns = columnMappings.map(r => ({SourceField: r.field, TargetField: r.alias, Condition: r.Condition}));
        }
        let uploadUrl = (await axios.post(Config.api + `/api/v2/product/catalog/createimport`, request)).data.Body;
        await axios.post(uploadUrl, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
        });
    }catch(e){
        throw new Error('Failed to upload file.');
    }
}

function CatalogDropZone(props: any) {
    let {file, onDrop} = props;
    return <DropZone accept=".csv, .xlsx, .txt" style={{width: '100%', height: '5em'}} onDrop={onDrop}>
    {({ isDragAccept, isDragReject, acceptedFiles, rejectedFiles }) => {
        if (isDragAccept || isDragReject) {
          return "Drop here to upload this file.";
        }
        return file ? file.name : "Drop a CSV/XLSX catalog file here or click to choose a file.";
      }}
    </DropZone>;
}

function CatalogImportTable(props: any) {
    const [state, setStateRaw] = useState<any>({showEdit: false, mapping: {}});
    const setState = (update: any) => setStateRaw((prev: any) => ({ ...prev, ...(typeof update === 'function' ? update(prev) : update) }));
    const convertToGroup = ([name], formState, { changeValue }) => {
        changeValue(formState, name, value => ({Logic: 'and', Conditions: [{...value}]}));
    };
    const setColumnMapping = (values) => {
        props.setColumnMapping(state.mappingIndex, values);
        setState({showEdit: false});
    };

    let {validationWarnings, profile, attrOptions, newAttribute, parsedFields, removeMapping, file} = props;
    let fieldOptions = (parsedFields || []).map(r => ({value: r.field, label: r.field}));
    return <>
    {validationWarnings && validationWarnings.length > 0 ?
        <Fragment>
    {validationWarnings.map((r, i) => <Notifier color="#FFB600" style={{marginBottom: '5px'}} key={i}>{r}</Notifier>)}
    </Fragment>
    : ''}
    <div style={{marginTop: '0.5em', textAlign: 'left', marginLeft: '1em'}}>
        <Button variant='outlined' disabled={!file} onClick={() => setState({showEdit: true, mapping: {}, mappingIndex: null})}>Add Mapping</Button>
    </div>
    <Table className='itemTable'>
        <TableHead>
            <TableRow>
                <TableCell>Catalog Attribute</TableCell>
                <TableCell>Maps From</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell style={{width: '18em'}}></TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {profile && profile.Fields && profile.Fields.map((r, i) => ({r, i})).map(({r, i}) => <TableRow key={i}>
                <TableCell>{!r.alias ? '' : (attrOptions.find(x => x.value === r.alias) || {}).label || <span>{r.alias} <span style={{ color: '#EE3224' }}>This attribute does not exist.</span></span>}</TableCell>
                <TableCell>{(fieldOptions.find(x => x.value === r.field) || {}).label || (isValidExpression(r.field) && r.field) || <span>{r.field} <span style={{color: '#EE3224'}}>This field is not present in the uploaded file.</span></span>}</TableCell>
                <TableCell>{parseCondition(r.Condition, fieldOptions)}</TableCell>
                <TableCell>
                    <Button variant='outlined' onClick={() => setState({showEdit: true, mapping: r, mappingIndex: i})} style={{marginRight: '1em'}}>Edit</Button>
                    <Button variant='outlined' onClick={() => removeMapping(i)}>Remove</Button>
                </TableCell>
            </TableRow>)}
            {!file && (!profile || !profile.Fields) && <TableRow>
                <TableCell colSpan={4}>Please upload a file to start the mapping process.</TableCell>
            </TableRow>}
        </TableBody>
    </Table>
    <Dialog open={state.showEdit} onClose={() => {setState({showEdit: false})}} maxWidth='sm' fullWidth>
    <Form onSubmit={setColumnMapping}
    initialValues={state.mapping}
    mutators={{...arrayMutators, convertToGroup: convertToGroup}}
    render={({ handleSubmit, pristine, invalid, values, form: {mutators} }) => (
      <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
        <Toolbar className='lbtoolbar'>Edit Mapping</Toolbar>
        <DialogContent>
            <div className='fieldHolder'>
                    <div style={{display: 'flex'}}>
                        <Field style={{flex: '1'}} label='Catalog attribute' component={FastSelect} name='alias' options={attrOptions}/>
                        <div style={{alignSelf: 'center', marginRight: '0.5rem'}}>
                            <Button  className='tinyButton' color='primary' onClick={() => newAttribute({new: true, Name: state.mapping.field, Group: 'Imported', Type: 'text'})}>Add New</Button>
                        </div>
                    </div>
                    <div>
                        <Field label='Source field expression' helperText="Please enclose constant values in quotes." component={AutoComplete as any} name='field' options={fieldOptions} validate={v => getErrorMessage(v)}/>
                    </div>
            </div>
            <Typography>Conditions</Typography>
            <RecursiveConditionField name='Condition' value={values.Condition} options={fieldOptions} mutators={mutators}/>
        </DialogContent>
        <DialogActions>
            <Button color='primary' type='submit' disabled={pristine || invalid || state.disableEdit}>Continue</Button>
        </DialogActions>
        </form>)}/>
    </Dialog>
    </>;
}

function CatalogImport(props: any) {
    const [state, setStateRaw] = useState<any>({loaded: false, attributes: [], showExport: false, showAttributeEdit: false,
    showError: false, showSave: false, showSaveAs: false, showShare: false, showEdit: false, confirmDelete: false,
    profile: {}, profiles: [], showFilter: false, showImport: false, showExportIfAble: false, exportIfAble: null,
    mappedPartners: [], showProfileImport: false, showRuleEdit: false});
    // Single setter: accepts an object partial OR a functional updater
    // `(prev) => partial`. The updater form lets read-modify-write handlers read
    // the latest state (parity with the old synchronous stateRef mirror) without
    // reaching for the raw setter. Merge matches the old class this.setState.
    const setState = (update: any) => setStateRaw((prev: any) => ({ ...prev, ...(typeof update === 'function' ? update(prev) : update) }));
    const profileSelectRef = useRef<any>(null);
    const setRuleRef = useRef<any>(undefined);

    const reloadProfiles = async () => {
        let profiles = await loadProfiles(profileType);
        setState({profiles, loaded: true});
        return profiles;
    };

    const componentDidMount = async () => {
        await loadPartnerConfigs();
        await loadAttributes();
        await reloadProfiles();
    };

    useEffect(() => {
        componentDidMount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadPartnerConfigs = async () => {
        let partners = (await axios.get(Config.api + '/api/v1/Partners')).data.Body.Partners.sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        let allSettings = (await axios.get(Config.api + `/odata/Company/Functions.GetSystemSettings?system=Catalog`)).data;
        let settings = ((allSettings || {}).PartnerProperties) || [];
        let mappedPartners = partners.map(r => ({CompanyName: r.CompanyName, Template: (settings.find(x => x.PartnerCoId === r.Id) || {}).CatalogExportTemplate}));
        mappedPartners = mappedPartners.filter(r => !(!r.Template));
        setState({mappedPartners});
    };
    const loadAttributes = async () => {
        let attributes = (await axios.get(Config.api + '/api/v1/product/catalog/attributes')).data.Records;
        let attrOptions = attributes.map(r => ({label: r.FriendlyName, value: r.Name}));
        setState({attributes, attrOptions});
    };
    const setColumnMapping = (index, e) => {
        let value = e;
        // Functional updater so a concurrent mapping edit reads the latest
        // profile.Fields (parity with the old synchronous stateRef mirror).
        setState((prev: any) => {
            let profile = {...prev.profile};
            let mappings = [...prev.profile.Fields];
            if(index === null){
                mappings.push(value);
            } else {
                mappings[index] = {...mappings[index]};
                mappings[index].alias = value.alias;
                mappings[index].Condition = value.Condition;
                mappings[index].field = value.field;
            }
            profile.Fields = mappings;
            return {profile};
        });
    };
    const removeMapping = (index) => {
        setState((prev: any) => {
            let profile = {...prev.profile};
            let mappings = [...prev.profile.Fields];
            mappings.splice(index, 1);
            profile.Fields = mappings;
            return {profile};
        });
    };
    const getProfile = () => {
        return {...state.profile};
    };
    const onDelete = async () => {
        await reloadProfiles();
        profileSelectRef.current.setValue('');
        setState((prev: any) => ({profile: { Name: '', DocumentType: 'Catalog', Fields: prev.parsedFields.map(r => ({...r}))}, confirmDelete: false}));
    };

    const onSave = async () => {
        let profiles = await reloadProfiles();
        setState({showSave: false});
        return profiles;
    };

    const onSaveAs = async (newName) => {
        let profiles = await reloadProfiles();
        profileSelectRef.current.setValue(newName);
        setState({showSaveAs: false});
        onProfileSelect(newName, profiles);
    };

    const showSave = async () => {
        let values = getProfile();
        if(!values.Id){
            await saveProfile(values, profileType);
            let profiles = await onSave();
            onProfileSelect(values.Name, profiles);
        } else {
            setState({showSave: true});
        }
    };

    const showSaveAs = () => {
        setState({showSaveAs: true});
    };
    const onProfileSelect = (name, profiles: any = state.profiles) => {
        if(!name){
            return;
        }
        let selectedProfile = profiles.find(r => r.Name === name);
        let profile = {...selectedProfile};
        delete profile.Filter;
        let oldProfile = JSON.parse(JSON.stringify(profile));
        profile = JSON.parse(JSON.stringify(profile));
        setState({profile, oldProfile});
    };
    const shared = async (name) => {
        let profiles = await reloadProfiles();
        setState({showShare: false});
        onProfileSelect(name, profiles);
    };
    const onDrop = async files => {
        if(!files || !files.length){
            return;
        }
        let file = files[0];
        setState({file: file, loaded: false, profile: {DocumentType: 'Catalog'}, parsedFields: [], parsed: false, parseError: null});
        try{
            let result = await uploadFile(file, state.attributes);
            setState({profile: result.profile, oldProfile: result.oldProfile, loaded: true, parsedFields: result.parsedFields, showError: false, validationWarnings: result.validationWarnings, originalAttributes: result.originalAttributes});
            profileSelectRef.current.setValue('');
        }catch(e){
            setState({loaded: true, showError: true, error: e.message});
        }
    };
    const broadcastImport = async (exportIfAble) => {
        setState({exportIfAble, showExportIfAble: false});
        await runImport(exportIfAble);
    };
    const runImport = async (exportIfAble: any = state.exportIfAble) => {
        if(state.mappedPartners && state.mappedPartners.length > 0 && exportIfAble !== true && exportIfAble !== false){
            setState({showExportIfAble: true});
            return;
        }
        try{
            await importFile(state.file, state.profile, exportIfAble);
            let catalogUploadMessage = 'Catalog file uploaded. The import process may take a few minutes to complete. Once it completes you must send any updates to the retailer from the catalog page.';
            if(exportIfAble){
                catalogUploadMessage = 'Catalog file uploaded. The import process may take a few minutes to complete.';
            }
            setState({showImport: true, exportIfAble: null, catalogUploadMessage});
        }catch(e){
            setState({showError: true, error: e.message, exportIfAble: null});
        }
    };
    const showNewAttribute = (attr) => {
        setState({attribute: attr, showAttributeEdit: true});
    };
    const handleSaveAttribute = async (values) => {
        try{
            let saved = await saveAttribute(values);
            await loadAttributes();
            let index = state.profile.Fields.findIndex(r => r.field === state.attribute.Name);
            if(index > -1){
                // Point the row for this source field at the newly created attribute:
                // set its alias while preserving the existing field/Condition. Passing
                // saved.Name (a string) blanked all three (value.alias === undefined).
                setColumnMapping(index, {...state.profile.Fields[index], alias: saved.Name});
            }
            setState({showAttributeEdit: false, attribute: null});
        }catch(e){
            setState({showError: true, error: e.message});
        }
    };
    const exportProfile = () => {
        let profile = getProfile();
        let config = JSON.stringify(profile);
        let fileName = 'catalog-import-profile.json';
        if(state.profile && state.profile.Name){
            fileName = `catalog-import-profile-${state.profile.Name}.json`;
        }
        let blob = new Blob([config], { type: "application/octet-stream" });
        FileSaver.saveAs(blob, fileName);
    };

    const showImportProfile = () => {
        setState({showProfileImport: true, newProfileName: '', importProfile: null, disableProfileImport: false});
    };

    const onProfileImportLoad = async (reader, name) => {
        let text = reader.result;
        let parsed = JSON.parse(text);
        parsed.Name = name;
        delete parsed.Id;
        delete parsed.Shared;
        await saveProfile(parsed, profileType);
        await onSaveAs(name);
        setState({showProfileImport: false});
    };

    const importProfile = (name, file) => {
        setState({disableProfileImport: true});
        let reader = new FileReader();
        reader.onload = onProfileImportLoad.bind(null, reader, name);
        reader.readAsText(file);
    };

    const onProfileDrop = (files) => {
        if(files && files.length > 0){
            setState({importProfile: files[0]});
        }
    };

    let {isRetailer} = props;
    return <Grid container spacing={2}>
        <Grid size={12}>
            <Card>
                <Toolbar className='lbtoolbar'>Catalog File</Toolbar>
                <CardContent>
                    <CatalogDropZone onDrop={onDrop} file={state.file}/>
                </CardContent>
            </Card>
        </Grid>
        <Grid size={12} style={{textAlign: 'left'}}>
        <Card>
            <Toolbar className='lbtoolbar'>Profile</Toolbar>
            {!state.loaded && <LinearProgress style={{height: '5px'}}/>}
                <CardContent>
                    <div style={{display: 'flex'}}>
                        <ExtendedSelect disabled={!state.file} innerRef={profileSelectRef} variant='outlined' style={{flex: '1 1 auto', display: 'flex'}} placeholder='Select profile...' value={state.profile.Name || ''} onChange={onProfileSelect} options={state.profiles.map(r => {return {label: r.Name, value: r.Name}})}/>
                        <DownloadButton disabled={!state.profile.Name || !(!state.profile.PartnerCompanyName) || isEqual(state.profile, state.oldProfile)} style={{marginRight: '1em', marginLeft: '1em'}} variant='outlined' onClick={showSave}>Save</DownloadButton>
                        <Button style={{marginRight: '1em'}} variant='outlined' onClick={showSaveAs}>Save As</Button>
                        <Button disabled={!state.profile.Name || !(!state.profile.PartnerCompanyName) || (!props.isRetailer && !(!state.profile.Id))} style={{marginRight: '1em'}} onClick={() => setState({showShare: true})} variant='outlined'>Share</Button>
                        <Button disabled={!state.profile.Name || !(!state.profile.PartnerCompanyName)} style={{marginRight: '1em'}} variant='outlined' onClick={() => setState({confirmDelete: true})}>Delete</Button>
                        <Button disabled={!state.profile.Name} style={{marginRight: '1em'}} variant='outlined' onClick={exportProfile}>Save to file</Button>
                        <Button style={{marginRight: '1em'}} variant='outlined' onClick={showImportProfile}>Load from file</Button>
                        <DownloadButton disabled={!state.file} color='primary' variant='contained' onClick={() => runImport()}>Import</DownloadButton>
                    </div>
                    <div style={{textAlign: 'left', marginTop: '0.5em'}}>
                        {state.profile.Description}
                    </div>
                </CardContent>
        </Card>
        </Grid>
        <Grid size={12} style={{textAlign: 'left'}}>
        <Card>
            <Toolbar className='lbtoolbar'>Column Mapping</Toolbar>
            {!state.loaded && <LinearProgress style={{height: '5px'}}/>}
            {state.validationWarnings && state.validationWarnings.length > 0 ?
                <Fragment>
            {state.validationWarnings.map((r, i) => <Notifier color="#FFB600" style={{marginBottom: '5px'}} key={i}>{r}</Notifier>)}
            </Fragment>
            : ''}
            <CatalogImportTable newAttribute={showNewAttribute}
                parsedFields={state.parsedFields}
                file={state.file}
                setColumnMapping={setColumnMapping}
                removeMapping={removeMapping}
                profile={state.profile}
                attrOptions={state.attrOptions}
                validationWarnings={state.validationWarnings}/>
        </Card>
        </Grid>
        <Grid size={12}>
            <Card>
                <Toolbar className='lbtoolbar'>Feed Rules</Toolbar>
                <RuleEditor onChange={rules => setState((prev: any) => ({profile: {...prev.profile, Rules: rules}, attrOptions: getAttrOptions(rules, prev.originalAttributes)}))} value={state.profile.Rules || []} disabled={!state.file} fieldOptions={state.originalAttributes}/>
            </Card>
        </Grid>
        <Dialog open={state.showError} onClose={() => {setState({showError: false})}}>
            <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
            <DialogContent><Typography style={{whiteSpace: "pre-wrap"}}>{state.error}</Typography></DialogContent>
        </Dialog>
        <Dialog open={state.showImport} onClose={() => {setState({showImport: false})}}>
            <Toolbar className='lbtoolbar'>{'File Uploaded'}</Toolbar>
            <DialogContent><Typography style={{whiteSpace: "pre-wrap"}}>{state.catalogUploadMessage}</Typography></DialogContent>
            <DialogActions>
                <Button color='primary' component={Link} to='/catalog'>Go to catalog</Button>
            </DialogActions>
        </Dialog>
        <Dialog open={state.showExportIfAble} onClose={() => {setState({showExportIfAble: false})}}>
            <Toolbar className='lbtoolbar'>{'Send to Retailers?'}</Toolbar>
            <DialogContent>
                <Typography style={{whiteSpace: "pre-wrap"}}>You can send the items in this feed to these partners since you have export profiles configured:</Typography>
                <Typography style={{whiteSpace: "pre-wrap", marginTop: '10px', marginBottom: '10px'}}>{state.mappedPartners.map(r => `${r.CompanyName} using profile ${r.Template}\n`)}</Typography>
                <Typography style={{whiteSpace: "pre-wrap"}}>Would you like to export enabled items from this feed after the import proces is complete?</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => broadcastImport(false)}>No, I'll export later</Button>
                <Button color='primary' onClick={() => broadcastImport(true)}>Yes, send to my retailers</Button>
            </DialogActions>
        </Dialog>
        <Dialog open={state.showProfileImport} onClose={() => {setState({showProfileImport: false})}} maxWidth='md' fullWidth={true}>
            <Toolbar className='lbtoolbar'>{'Import Profile'}</Toolbar>
            <DialogContent style={{overflow: "hidden"}}>
                <MuiTextField style={{width: '100%', marginBottom: '1em'}} placeholder='New profile name' value={state.newProfileName} onChange={(e) => setState({newProfileName: e.target.value})}/>
                <DropZone accept=".json, .txt" style={{width: '100%', height: '10em'}} onDrop={onProfileDrop}>
                {({ isDragAccept, isDragReject, acceptedFiles, rejectedFiles }) => {
                    if (isDragAccept || isDragReject) {
                      return "Drop here to upload this file.";
                    }
                    return state.importProfile ? state.importProfile.name : 'Drop an import profile here or click to choose a file.';
                  }}
                </DropZone>
            </DialogContent>
            <DialogActions><Button color='primary' disabled={!state.newProfileName || state.disableProfileImport || !state.importProfile} onClick={() => importProfile(state.newProfileName, state.importProfile)}>Import</Button></DialogActions>
        </Dialog>
        <MappingEditor title="Rule" open={state.showRuleEdit} onClose={() => {setState({showRuleEdit: false})}}
        initialValues={state.rule} sourceOptions={state.attrOptions} targetOptions={state.attrOptions} allowNewTarget={true}
        targetName='TargetField' targetLabel='Attribute' sourceName='SourceField' onSubmit={setRuleRef.current} disableEdit={state.disableEdit}/>
        <DeleteDialog open={state.confirmDelete} onClose={() => setState({confirmDelete: false})} onDelete={onDelete} profileType={profileType} values={getProfile()}/>
        <ShareDialog isRetailer={isRetailer} open={state.showShare} onClose={() => setState({showShare: false})} onShare={shared} profileType={profileType} values={getProfile()}/>
        <SaveAsDialog open={state.showSaveAs} onClose={() => setState({showSaveAs: false})} onSaveAs={onSaveAs} profileType={profileType} values={getProfile()}/>
        <SaveDialog open={state.showSave} onClose={() => setState({showSave: false})} onSave={onSave} profileType={profileType} values={getProfile()}/>
        <AttributeEditor open={state.showAttributeEdit} onClose={() => setState({showAttributeEdit: false})} item={state.attribute} save={handleSaveAttribute} disableEdit={state.disableAttributeEdit}/>
    </Grid>;
}

export default withCatalogEnabled(CatalogImport);
export {setDefaultMappings, CatalogDropZone, CatalogImportTable, uploadFile, profileType, importFile};
